import { useToolOwnership } from "../shared/toolOwnership"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAppMessage } from "./callTransport"
import { meetings } from "services/api/meetingsClient"
import type { MeetingInspirePanel } from "services/api/meetingsClient"
import type { InspireControlAction } from "../../inspireControlMessages"
import { useMeetingPanelEvents } from "../../useMeetingPanelEvents"
import { getPanelTokenFromUrl } from "../shared/panelUrls"
import { INSPIRE_LISTENER_RECONCILE_INTERVAL_MS, INSPIRE_SYNC_MESSAGE_TYPE } from "./constants"

type UseInspireSyncArgs = {
  meetingId: string
  panel?: MeetingInspirePanel
  enabled: boolean
  preservePresentation?: boolean
  onActivityVisibilityChange?: (visible: boolean) => void
}

export type InspireSyncMessage = {
  type: typeof INSPIRE_SYNC_MESSAGE_TYPE
  meetingId: string
  activityId: string
  action: InspireControlAction
}

/**
 * Keeps Inspire Cards in sync between the presenter and everyone else in custom mode.
 *
 * - Presenter actions (start/stop/share/unshare/close) are broadcast over Daily app messages.
 * - Listeners react to those messages immediately, and also reconcile from the Inspire panel
 *   API on WebSocket `inspire.*` events and a 30 s poll, so a missed message never strands them.
 * - "Visible" means an activity or shared response should be in front of the attendee.
 */
export function useInspireSync({ meetingId, panel, enabled, preservePresentation = false, onActivityVisibilityChange }: UseInspireSyncArgs) {
  const { capture } = useToolOwnership(`${meetingId}:${panel?.role}:${panel?.url}`)
  const role = panel?.role
  const panelToken = useMemo(() => getPanelTokenFromUrl(panel?.url), [panel?.url])
  const listenerEnabled = Boolean(enabled && panel?.enabled && role === "listener" && meetingId && panelToken)
  // Presenters drive their own state through their actions, but a reload while a card is live must
  // still put the card back on their stage, so they reconcile from the API (and its realtime events) too.
  const reconcileEnabled = Boolean(enabled && panel?.enabled && meetingId && panelToken)
  const [active, setActive] = useState(false)
  /** A card is being collected or a response is shared: something worth putting on the stage. */
  const [contentLive, setContentLive] = useState(false)
  const [realtimeUrl, setRealtimeUrl] = useState<string | null>(null)
  const [syncVersion, setSyncVersion] = useState(0)
  const activeRef = useRef(false)
  /** Bumped on every local presenter action so a panel fetch issued before it cannot undo it. */
  const actionEpochRef = useRef(0)
  const reconcileRequestRef = useRef(0)
  const visibilityCallbackRef = useRef(onActivityVisibilityChange)
  visibilityCallbackRef.current = onActivityVisibilityChange

  const setVisible = useCallback((next: boolean) => {
    if (activeRef.current === next) return
    activeRef.current = next
    setActive(next)
    visibilityCallbackRef.current?.(next)
  }, [])

  const reconcile = useCallback(async (forcedVisibility?: boolean) => {
    const owns = capture()
    if (!reconcileEnabled) return
    const request = ++reconcileRequestRef.current
    if (typeof forcedVisibility === "boolean") {
      setVisible(forcedVisibility)
      setContentLive(forcedVisibility)
    }
    const epoch = actionEpochRef.current
    try {
      const payload = await meetings.getMeetingInspirePanel(meetingId, panelToken)
      if (!owns() || epoch !== actionEpochRef.current || request !== reconcileRequestRef.current) return
      setRealtimeUrl(payload.realtime_url || null)
      const hasContent = Boolean(payload.active_activity || payload.shared_activity)
      if (typeof forcedVisibility !== "boolean") {
        setVisible(hasContent)
        setContentLive(hasContent)
      } else if (forcedVisibility && !hasContent) {
        // The presenter's message arrived before the API caught up; keep the forced state.
      } else {
        setContentLive(forcedVisibility && hasContent)
      }
    } catch {
      // Keep the current view; realtime and the next polling pass can reconcile it.
    }
  }, [reconcileEnabled, meetingId, panelToken, setVisible, capture])

  useEffect(() => {
    if (!reconcileEnabled) {
      reconcileRequestRef.current += 1
      if (!preservePresentation) { setVisible(false); setContentLive(false) }
      setRealtimeUrl(null)
      return
    }
    void reconcile()
    const intervalId = window.setInterval(() => void reconcile(), INSPIRE_LISTENER_RECONCILE_INTERVAL_MS)
    return () => {
      window.clearInterval(intervalId)
      reconcileRequestRef.current += 1
    }
  }, [reconcileEnabled, reconcile, setVisible, preservePresentation])

  useMeetingPanelEvents({ onReconnect: () => void reconcile(), panelToken,
    realtimeUrl,
    enabled: reconcileEnabled,
    onEvent: (event) => {
      if ((event.type.startsWith("inspire.") || event.type.startsWith("poll."))) void reconcile()
    },
  })

  const handleAppMessage = useCallback((event: any) => {
    const data = event?.data as (Omit<Partial<InspireSyncMessage>, "type"> & { type?: string }) | undefined
    if (data?.type === "wisefriend.poll-sync" && data?.meetingId === meetingId) { void reconcile(); return }
    if (!listenerEnabled || data?.type !== INSPIRE_SYNC_MESSAGE_TYPE || data?.meetingId !== meetingId) return
    setSyncVersion((version) => version + 1)
    if (data.action === "close") {
      void reconcile(false)
      return
    }
    if (data.action === "start" || data.action === "share") {
      // A new card or a shared response must come back in front of every attendee, including
      // those who closed the drawer while the session stayed active; setVisible only fires the
      // callback on a transition, so re-surface explicitly when already visible.
      const alreadyVisible = activeRef.current
      void reconcile(true)
      if (alreadyVisible) visibilityCallbackRef.current?.(true)
      return
    }
    void reconcile()
  }, [listenerEnabled, meetingId, reconcile])

  const sendAppMessage = useAppMessage({ onAppMessage: handleAppMessage })

  const sendControlAction = useCallback(async (action: InspireControlAction, activityId: string) => {
    if (!meetingId) return false
    if (role === "presenter") {
      actionEpochRef.current += 1
      if (action === "start" || action === "share") {
        setVisible(true)
        setContentLive(true)
      }
      if (action === "stop" || action === "unshare") { setVisible(false); setContentLive(false) }
      if (action === "close") {
        setVisible(false)
        setContentLive(false)
      }
    }
    try {
      sendAppMessage({ type: INSPIRE_SYNC_MESSAGE_TYPE, meetingId, activityId, action } as InspireSyncMessage, "*")
    } catch {
      return false
    }
    return true
  }, [meetingId, role, sendAppMessage, setVisible])

  /** Re-reads the card state and refreshes the card views, for server-side changes this page caused. */
  const refresh = useCallback(() => {
    setSyncVersion((version) => version + 1)
    void reconcile()
  }, [reconcile])

  return { inspireActive: active, inspireContentLive: contentLive, sendControlAction, syncVersion, refresh }
}
